(() => {
  // Demo signups stay in the browser. Live signups (a theme setting, off by default) post to Klaviyo's
  // client subscriptions endpoint, which is built for browsers and takes only the public site ID.
  const KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_REVISION = '2026-07-15';

  // Reuse the store's existing pixel. Track accepted live signups, never demo activity.
  const META_PIXEL_ID = '803784176334385';
  const trackedLeadEmails = new Set(); // Page memory only; never sent to Meta or persisted.
  const trackAcceptedLead = (submittedEmail, attemptsLeft = 20) => {
    const signupKey = submittedEmail.toLowerCase();
    if (trackedLeadEmails.has(signupKey)) return;
    try {
      if (window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main') return;
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof window.fbq !== 'function') {
        // Allow the existing pixel a short time to load; never delay the signup confirmation.
        if (attemptsLeft > 0) setTimeout(() => trackAcceptedLead(submittedEmail, attemptsLeft - 1), 500);
        return;
      }
      if (!privacy.analyticsProcessingAllowed?.() || !privacy.marketingAllowed?.() || !privacy.saleOfDataAllowed?.()) return;
      window.fbq('trackSingle', META_PIXEL_ID, 'Lead', { content_name: 'Harmonize waitlist' });
      trackedLeadEmails.add(signupKey);
    } catch {
      // A blocked or failing pixel must never turn an accepted signup into a form error.
    }
  };

  const subscribe = async ({ key, list, source, email }) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${KLAVIYO_URL}?company_id=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        }),
        signal: controller.signal
      });
      const accepted = response.status === 202;
      const failure = accepted ? null : await response.json().catch(() => null);
      return {
        ok: accepted,
        status: response.status,
        code: failure?.errors?.[0]?.code,
        invalidEmail: Array.isArray(failure?.errors) && failure.errors.some(item =>
          item.source?.pointer === '/data/attributes/profile/data/attributes/email'
        )
      };
    } finally {
      clearTimeout(timer);
    }
  };

  const mountSignup = (signup) => {
    const live = signup.hasAttribute('data-teaser-live');
    const demo = signup.querySelector('[data-teaser-demo]');
    const email = signup.querySelector('[data-teaser-email]');
    const trap = signup.querySelector('[data-teaser-trap]');
    const fallback = signup.querySelector('[data-teaser-fallback] form');
    const error = signup.querySelector('[data-teaser-error]');
    const confirmation = signup.querySelector('[data-teaser-confirmation]');
    const submit = signup.querySelector('[data-teaser-submit]');
    const submitLabel = signup.querySelector('[data-teaser-submit-label]');
    const idleLabel = submitLabel.textContent;
    const note = email.getAttribute('aria-describedby') || '';
    let busy = false;
    const setError = (message, invalidEmail = true) => {
      error.textContent = message;
      error.hidden = false;
      if (invalidEmail) email.setAttribute('aria-invalid', 'true'); else email.removeAttribute('aria-invalid');
      email.setAttribute('aria-describedby', `${note} ${error.id}`.trim());
      email.focus();
    };
    // Every failure shows the same message, so record the actual cause for support:
    // the browser console and data-teaser-failure on the form.
    const fail = (reason, message = error.dataset.failed) => {
      signup.dataset.teaserFailure = reason;
      console.warn(`[Emme teaser] Signup not sent: ${reason}`);
      setError(message, false);
    };
    const clearError = () => {
      email.removeAttribute('aria-invalid');
      if (note) email.setAttribute('aria-describedby', note); else email.removeAttribute('aria-describedby');
      error.hidden = true;
    };
    const setBusy = (state) => {
      busy = state;
      submit.disabled = state;
      submit.setAttribute('aria-busy', String(state));
      submitLabel.textContent = state ? signup.dataset.sending || idleLabel : idleLabel;
    };
    const confirm = () => {
      email.value = '';
      demo.hidden = true;
      confirmation.hidden = false;
      confirmation.focus();
    };
    // Blockers that stop a.klaviyo.com leave the store's own domain alone, so a blocked signup goes
    // through the Shopify newsletter form instead. Shopify's spam check is bound to that form only now,
    // so visitors whose Klaviyo request succeeds never load it.
    const sendFallback = (address, reason) => {
      signup.dataset.teaserFailure = `${reason}; sent through the store newsletter form`;
      console.warn(`[Emme teaser] ${signup.dataset.teaserFailure}`);
      fallback.querySelector('[data-teaser-fallback-email]').value = address;
      const giveUp = () => {
        setBusy(false);
        fail(`${reason}; the store newsletter form did not submit`, error.dataset.blocked || error.dataset.failed);
      };
      const timer = setTimeout(giveUp, 20000);
      const submitForm = () => {
        try {
          if (typeof fallback.requestSubmit === 'function') fallback.requestSubmit(); else fallback.submit();
        } catch {
          clearTimeout(timer);
          giveUp();
        }
      };
      const captcha = window.Shopify?.captcha;
      if (typeof captcha?.protect === 'function') captcha.protect(fallback, submitForm); else submitForm();
    };
    const send = async () => {
      if (busy) return;
      email.value = email.value.trim();
      const valid = email.checkValidity() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
      if (!valid) { setError(error.dataset.invalid); return; }
      clearError();
      if (!live) { confirm(); return; }
      if (trap.value) { trap.value = ''; fail('hidden spam-check field was filled, likely by browser autofill'); return; }
      setBusy(true);
      const submittedEmail = email.value;
      let handedOff = false;
      try {
        const result = await subscribe({ key: signup.dataset.klaviyoKey, list: signup.dataset.klaviyoList, source: signup.dataset.klaviyoSource, email: submittedEmail });
        if (result.ok) {
          confirm();
          trackAcceptedLead(submittedEmail);
        }
        else if (result.invalidEmail) setError(error.dataset.invalid);
        else fail(`Klaviyo responded ${result.status}${result.code ? ` (${result.code})` : ''}`);
      } catch (err) {
        const reason = `request to Klaviyo was blocked or failed (${err?.message || err})`;
        if (err?.name === 'AbortError') fail('Klaviyo did not respond within 10 seconds');
        // No response at all: usually a privacy or ad blocker (AdGuard and Ghostery block a.klaviyo.com).
        else if (fallback) { handedOff = true; sendFallback(submittedEmail, reason); }
        else fail(reason, error.dataset.blocked || error.dataset.failed);
      } finally {
        if (!handedOff) setBusy(false);
      }
    };
    submit.addEventListener('click', send);
    email.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); send(); } });
    email.addEventListener('input', () => { if (!error.hidden) clearError(); });
    signup.querySelector('[data-teaser-reset]')?.addEventListener('click', () => {
      confirmation.hidden = true;
      demo.hidden = false;
      clearError();
      email.focus();
    });
    // Back from the newsletter-form fallback: Shopify redirects here with this form's ID as the hash
    // and marks the outcome inside the form.
    if (live && fallback && window.location.hash === `#${fallback.id}`) {
      if (fallback.querySelector('[data-teaser-fallback-posted]')) confirm();
      else if (fallback.querySelector('[data-teaser-fallback-errors]')) fail('the store newsletter form rejected the signup');
    }
  };

  // One testimonial at a time once scripts run; without them, every quote stays visible.
  const mountQuotes = (root) => {
    const list = root.querySelector('.ed-teaser-quote-list');
    const quotes = [...root.querySelectorAll('[data-teaser-quote]')];
    const controls = root.querySelector('[data-teaser-quote-controls]');
    if (!controls || quotes.length < 2) return null;
    let index = 0;
    const show = (next) => {
      index = (next + quotes.length) % quotes.length;
      quotes.forEach((quote, i) => { quote.hidden = i !== index; });
    };
    list.classList.add('is-carousel');
    controls.hidden = false;
    controls.querySelectorAll('[data-teaser-quote-step]').forEach(button => {
      button.addEventListener('click', () => show(index + Number(button.dataset.teaserQuoteStep)));
    });
    show(0);
    return (block) => { const i = quotes.indexOf(block); if (i >= 0) show(i); };
  };

  const mount = (main) => {
    if (main.dataset.mounted) return;
    main.dataset.mounted = 'true';
    main.querySelectorAll('[data-teaser-signup]').forEach(mountSignup);
    const quotes = main.querySelector('[data-teaser-quotes]');
    main.teaserSelectQuote = quotes ? mountQuotes(quotes) : null;
  };
  const roots = (scope) => scope.querySelectorAll('[data-ed-teaser]');
  roots(document).forEach(mount);
  document.addEventListener('shopify:section:load', event => roots(event.target).forEach(mount));
  document.addEventListener('shopify:block:select', event => {
    const main = event.target.closest('[data-ed-teaser]');
    if (main?.teaserSelectQuote) main.teaserSelectQuote(event.target);
  });

  // Keep the footer clear of Shopify's preview bar.
  let previewBar;
  const syncInset = () => document.body.style.setProperty('--ed-teaser-preview-inset', `${previewBar?.getBoundingClientRect().height || 0}px`);
  const resize = new ResizeObserver(syncInset);
  const observePreview = () => {
    const next = document.getElementById('PBarNextFrameWrapper');
    if (next === previewBar) return;
    resize.disconnect(); previewBar = next;
    if (previewBar) resize.observe(previewBar);
    syncInset();
  };
  new MutationObserver(observePreview).observe(document.body, { childList: true });
  observePreview();
})();
